/**
 * actaPublicada — poner el acta donde Word para la web pueda abrirla.
 *
 * Word para la web no abre bytes: abre archivos. Es un host WOPI y necesita un
 * documento que viva en OneDrive o SharePoint, con su URL y sus permisos. El
 * acta que genera la aplicación no vive en ningún sitio —sale como base64 de la
 * API y se guarda en la base de datos—, así que para verla con el aspecto real
 * de Word hay que subirla antes. No hay atajo: no existe forma de empotrar un
 * .docx que solo está en memoria.
 *
 * Se puede editar ahí, y eso abre un riesgo que hay que cerrar: mientras la
 * copia de OneDrive se edita, la que tiene la aplicación —la que se descarga y
 * la que enseñan las zonas— se queda como estaba, y aparecen dos versiones del
 * mismo acta sin que nadie sepa cuál manda.
 *
 * Por eso existe `bajarDocumento`: al volver de Word se trae el archivo y la
 * aplicación se pone al día con él. El documento de la unidad es la versión
 * buena, y lo demás se deduce de él; no al revés.
 *
 * De ahí que la ruta sea determinista: el mismo acta va siempre al mismo
 * archivo. Graph reemplaza el contenido por ruta, así que volver a publicar no
 * acumula copias ni obliga a recordar identificadores en la base de datos.
 *
 * ── Dos destinos, y no son lo mismo ──────────────────────────────────────
 *
 * `publicarParaVer` deja una copia en el OneDrive de quien mira, solo para
 * poder empotrarla. Es privada, desechable y se reemplaza cada vez.
 *
 * `publicarEnBiblioteca` la lleva a la biblioteca del equipo, y eso es un acto
 * público: a partir de ahí la ve todo el que tenga acceso al sitio. Por eso son
 * dos funciones y no una con un parámetro —y por eso en la pantalla son dos
 * botones distintos—. Subir a la biblioteca por el mero hecho de echar un
 * vistazo publicaría borradores a todo el equipo sin que nadie lo pidiera.
 */

const GRAFO = 'https://graph.microsoft.com/v1.0';

/** La carpeta del OneDrive de la persona donde se dejan las actas. */
export const CARPETA = 'Actas';

export class OneDriveError extends Error {
  constructor(mensaje, { code = null, status = null, causa = null } = {}) {
    super(mensaje);
    this.name = 'OneDriveError';
    this.code = code;
    this.status = status;
    // El error de origen, cuando este envuelve a otro. No se enseña —a nadie le
    // sirve «Failed to fetch»— pero sin guardarlo se pierde lo unico que dice
    // que fallo de verdad, y el que lo tenga que mirar empieza de cero.
    this.causa = causa;
  }
}

async function leerRespuesta(respuesta) {
  const texto = await respuesta.text();
  const datos = texto ? JSON.parse(texto) : null;

  if (!respuesta.ok) {
    const error = datos && datos.error ? datos.error : {};
    // El caso que más va a pasar al principio: la aplicación pedía solo
    // permisos de lectura, y subir necesita Files.ReadWrite. Se nombra, porque
    // "403 Forbidden" no le dice a nadie que hay que volver a dar consentimiento.
    if (respuesta.status === 403 || error.code === 'accessDenied') {
      throw new OneDriveError(
        'OneDrive no dejó guardar el acta. Falta el permiso de escritura ' +
        '(Files.ReadWrite): cierra sesión y vuelve a entrar para concederlo.',
        { code: error.code || 'accessDenied', status: respuesta.status }
      );
    }
    // 423: el documento está abierto en Word y no admite escrituras. El mensaje
    // que manda el servicio —«The resource you are attempting to access is
    // locked»— no dice ni qué recurso ni quién lo tiene, que es todo lo que
    // hace falta saber para resolverlo.
    if (respuesta.status === 423 || error.code === 'resourceLocked' || error.code === 'notAllowed') {
      throw new OneDriveError(
        'El documento está abierto en Word y SharePoint no deja escribirlo mientras tanto. ' +
        'Cierra la pestaña o la aplicación de Word donde lo tengas y vuelve a intentarlo; ' +
        'el bloqueo tarda unos minutos en soltarse.',
        { code: error.code || 'resourceLocked', status: respuesta.status }
      );
    }

    throw new OneDriveError(error.message || respuesta.statusText, {
      code: error.code || null,
      status: respuesta.status
    });
  }

  return datos;
}

/**
 * Un nombre de archivo que OneDrive acepte.
 *
 * OneDrive rechaza `\ / : * ? " < > |` y los nombres que empiezan o acaban en
 * espacio o punto. Un acta se titula con el nombre de la reunión, y ahí caben
 * dos puntos ("Check Point 1:1") y barras ("Acuerdos / Tareas") sin que nadie
 * lo piense.
 */
export function nombreDeArchivo(titulo, sufijo = null) {
  let limpio = String(titulo || 'Acta')
    .replace(/[\\/:*?"<>|#%]/g, ' ')
    // Los caracteres de control son justo lo que hay que quitar de un nombre de
    // archivo, así que aquí la regla que los prohíbe está de más: un título
    // llegado de un calendario puede traer tabuladores o saltos de línea.
    // Por un espacio y no por nada: un salto de línea separa palabras, y
    // borrarlo dejaba "Junta\nde dirección" como "Juntade dirección".
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[.\s]+|[.\s]+$/g, '');

  if (!limpio) limpio = 'Acta';
  // Con el sufijo, dos sesiones de la misma reunión semanal no se pisan.
  if (sufijo) limpio = `${limpio} - ${sufijo}`;

  // OneDrive corta por 255; se deja margen para la extensión.
  return limpio.slice(0, 240) + '.docx';
}

/** La fecha como sufijo estable de nombre: "2026-09-15". */
export function sufijoDeFecha(fecha) {
  const d = fecha ? new Date(fecha) : null;
  if (!d || isNaN(d.getTime())) return null;
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Los bytes de un base64, que es como viaja el .docx desde la API. */
export function bytesDeBase64(base64) {
  const binario = atob(String(base64 || ''));
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes;
}

/**
 * Escribe el archivo en una unidad, reemplazando el que hubiera en esa ruta.
 *
 * `destino` es el trozo de Graph que nombra la unidad: `me/drive` para el
 * OneDrive de quien mira, `drives/{id}` para una biblioteca de SharePoint. Lo
 * demás es idéntico, y por eso está una sola vez.
 *
 * La subida simple de Graph vale hasta 250 MB y un acta pesa menos de uno, así
 * que no hace falta sesión de carga por trozos.
 *
 * @returns {{driveId: string, itemId: string, webUrl: string, nombre: string}}
 */
async function subir(token, destino, ruta, base64) {
  const codificada = ruta.split('/').map(encodeURIComponent).join('/');

  const respuesta = await fetch(`${GRAFO}/${destino}/root:/${codificada}:/content`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    },
    body: bytesDeBase64(base64)
  });

  // ── El archivo está abierto en Word ────────────────────────────────────
  //
  // SharePoint bloquea el documento mientras dura una sesión de edición y
  // responde 423 a cualquier escritura. Eso NO es un fallo: significa que el
  // acta ya está en su sitio y alguien la está escribiendo — exactamente lo que
  // queremos que pase. Tratarlo como error hacía que la pantalla se rindiera
  // justo cuando todo iba bien.
  //
  // Se usa el documento que ya hay. Lo que se iba a subir es, además, lo mismo
  // que la persona tiene delante: quien manda es el archivo.
  //
  // El bloqueo sobrevive a cerrar la pestaña —Word lo suelta unos minutos
  // después—, así que reintentar tampoco serviría.
  if (respuesta.status === 423) {
    const existente = await leerRespuesta(await fetch(
      `${GRAFO}/${destino}/root:/${codificada}`,
      { headers: { Authorization: `Bearer ${token}` } }
    ));
    return {
      driveId: (existente.parentReference && existente.parentReference.driveId) || null,
      itemId: existente.id,
      webUrl: existente.webUrl || null,
      nombre: existente.name || ruta.split('/').pop(),
      bloqueado: true
    };
  }

  const item = await leerRespuesta(respuesta);

  return {
    driveId: (item.parentReference && item.parentReference.driveId) || null,
    itemId: item.id,
    webUrl: item.webUrl || null,
    nombre: item.name || ruta.split('/').pop(),
    bloqueado: false
  };
}

/** La copia privada para poder mirarla. Va al OneDrive de quien la abre. */
export async function publicarParaVer(token, { base64, titulo, fecha = null }) {
  if (!base64) throw new OneDriveError('No hay documento que publicar');
  const nombre = nombreDeArchivo(titulo, sufijoDeFecha(fecha));
  return subir(token, 'me/drive', `${CARPETA}/${nombre}`, base64);
}

// ── La biblioteca del equipo ──────────────────────────────────

/**
 * El sitio y la carpeta donde se publican las actas.
 *
 * Se configura una vez por despliegue y no por persona: una biblioteca de actas
 * es una decisión de la organización, no una preferencia. Guardarla en las
 * preferencias del usuario habría hecho que cada uno publicara en un sitio
 * distinto, que es justo lo contrario de tener un lugar común.
 */
export function bibliotecaConfigurada() {
  const sitio = (process.env.REACT_APP_ACTAS_SITIO || '').trim();
  if (!sitio) return null;
  return {
    sitio,
    // El nombre de la biblioteca, si no es la predeterminada del sitio
    // ("Documentos compartidos").
    biblioteca: (process.env.REACT_APP_ACTAS_BIBLIOTECA || '').trim() || null,
    // Una barra sola significa la raíz de la biblioteca. Hace falta poder
    // decirlo: si la biblioteca ya se llama "Repo de Minutas", meter dentro otra
    // carpeta "Actas" es repetir la misma palabra dos veces en la ruta. Sin
    // valor se usa la carpeta por defecto, porque colgar actas de la raíz de una
    // biblioteca compartida sin que nadie lo haya pedido es peor.
    carpeta: carpetaConfigurada()
  };
}

function carpetaConfigurada() {
  const escrita = (process.env.REACT_APP_ACTAS_CARPETA || '').trim();
  if (escrita === '/' || escrita === '.') return '';
  return escrita || CARPETA;
}

/**
 * La URL de un sitio, partida como la quiere Graph.
 *
 * Graph no acepta la URL entera: pide el host y la ruta del servidor por
 * separado, `/sites/{host}:{ruta}`. Se saca aquí, aparte, porque es lo que
 * suele estar mal escrito en la configuración y conviene poder probarlo sin
 * llamar a nadie.
 */
export function partesDelSitio(url) {
  try {
    const u = new URL(String(url).trim());
    if (!u.hostname) return null;
    // Sin barra final: "/sites/Direccion/" y "/sites/Direccion" son el mismo
    // sitio, pero Graph responde 400 al segundo con barra.
    const ruta = u.pathname.replace(/\/+$/, '');
    return { host: u.hostname, ruta: ruta === '/' ? '' : ruta };
  } catch {
    return null;
  }
}

/**
 * Publica el acta en la biblioteca del equipo.
 *
 * A diferencia de la copia para mirar, esto es visible para todo el que tenga
 * acceso al sitio. Es una acción que pide la persona, no un efecto de abrir una
 * pantalla.
 */
export async function publicarEnBiblioteca(token, { base64, titulo, fecha = null }) {
  if (!base64) throw new OneDriveError('No hay documento que publicar');

  const config = bibliotecaConfigurada();
  if (!config) {
    throw new OneDriveError(
      'No hay biblioteca de actas configurada. Se define con REACT_APP_ACTAS_SITIO ' +
      '(la URL del sitio de SharePoint) al desplegar.',
      { code: 'SIN_CONFIGURAR' }
    );
  }

  const partes = partesDelSitio(config.sitio);
  if (!partes) {
    throw new OneDriveError(
      `La biblioteca configurada no es una URL válida: "${config.sitio}"`,
      { code: 'SIN_CONFIGURAR' }
    );
  }

  const sitio = await leerRespuesta(await fetch(
    `${GRAFO}/sites/${partes.host}:${partes.ruta}`,
    { headers: { Authorization: `Bearer ${token}` } }
  ));

  let driveId;
  if (config.biblioteca) {
    const drives = await leerRespuesta(await fetch(
      `${GRAFO}/sites/${sitio.id}/drives?$select=id,name`,
      { headers: { Authorization: `Bearer ${token}` } }
    ));
    // Se busca por el nombre que se ve y también por el que sale en la
    // dirección: una biblioteca llamada «Repo de Minutas» puede aparecer en la
    // URL como "Repo%20de%20Minutas" o como "RepoDeMinutas", y quien configura
    // esto copia lo que tiene delante, que casi siempre es la dirección.
    const buscado = config.biblioteca.toLowerCase();
    const comoEnLaUrl = (d) => {
      const trozo = String(d.webUrl || '').split('/').filter(Boolean).pop() || '';
      try { return decodeURIComponent(trozo).toLowerCase(); } catch { return trozo.toLowerCase(); }
    };
    const candidatas = drives.value || [];
    const elegida = candidatas.find(d => String(d.name || '').toLowerCase() === buscado)
      || candidatas.find(d => comoEnLaUrl(d) === buscado);

    if (!elegida) {
      throw new OneDriveError(
        `El sitio no tiene ninguna biblioteca llamada "${config.biblioteca}". ` +
        `Tiene: ${candidatas.map(d => d.name).join(', ') || 'ninguna'}.`,
        { code: 'SIN_CONFIGURAR' }
      );
    }
    driveId = elegida.id;
  } else {
    const drive = await leerRespuesta(await fetch(
      `${GRAFO}/sites/${sitio.id}/drive?$select=id`,
      { headers: { Authorization: `Bearer ${token}` } }
    ));
    driveId = drive.id;
  }

  const nombre = nombreDeArchivo(titulo, sufijoDeFecha(fecha));
  const ruta = config.carpeta ? `${config.carpeta}/${nombre}` : nombre;
  const item = await subir(token, `drives/${driveId}`, ruta, base64);

  return { ...item, sitio: sitio.webUrl || config.sitio, carpeta: config.carpeta };
}

/**
 * El sello del documento: cambia cada vez que se guarda.
 *
 * Sirve para saber si hay algo nuevo sin bajarse el archivo entero. Un acta
 * pesa cientos de miles de bytes y la comprobación se repite cada pocos
 * segundos mientras se edita; descargarla para comparar sería gastar megas por
 * minuto para casi siempre concluir que no ha cambiado nada.
 */
export async function selloDeDocumento(token, { driveId, itemId }) {
  const respuesta = await fetch(
    `${GRAFO}/drives/${driveId}/items/${itemId}?$select=eTag,lastModifiedDateTime,size`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const item = await leerRespuesta(respuesta);
  // El eTag es lo fiable; la fecha y el tamaño se añaden porque en OneDrive el
  // eTag puede repetirse entre guardados muy seguidos del mismo editor.
  return `${item.eTag || ''}|${item.lastModifiedDateTime || ''}|${item.size || 0}`;
}

/**
 * Baja el documento tal como está ahora en la unidad.
 *
 * Es lo que cierra el círculo cuando el acta se edita en Word: el archivo
 * cambió por fuera de la aplicación y hay que traérselo para que la pantalla y
 * las zonas dejen de ir por detrás.
 *
 * Se devuelve en base64 porque es como viaja el .docx por toda la aplicación
 * —así lo entrega la API y así lo espera la subida—, y convertirlo aquí evita
 * que cada sitio invente su propia conversión.
 */
export async function bajarDocumento(token, { driveId, itemId }) {
  const respuesta = await fetch(`${GRAFO}/drives/${driveId}/items/${itemId}/content`, {
    headers: { Authorization: `Bearer ${token}` }
  });

  if (!respuesta.ok) {
    throw new OneDriveError(
      `No se pudo descargar el documento de OneDrive (${respuesta.status}).`,
      { status: respuesta.status }
    );
  }

  return base64DeBytes(new Uint8Array(await respuesta.arrayBuffer()));
}

/**
 * Bytes a base64.
 *
 * A trozos y no de una: `String.fromCharCode(...bytes)` con un acta de varios
 * cientos de miles de bytes desborda la pila de argumentos y revienta.
 */
export function base64DeBytes(bytes) {
  let binario = '';
  const TROZO = 0x8000;
  for (let i = 0; i < bytes.length; i += TROZO) {
    binario += String.fromCharCode.apply(null, bytes.subarray(i, i + TROZO));
  }
  return btoa(binario);
}

/**
 * El documento convertido a PDF, por Office.
 *
 * Lo convierte el mismo motor que lo abre en Word para la web, así que sale con
 * la plantilla intacta: logos, encabezados, tablas, tipografías y los saltos de
 * página donde el documento los pone. Es la diferencia entre un PDF del acta y
 * un PDF que se le parece.
 *
 * Hacía falta un Word de verdad ejecutándose en algún sitio para esto, y por eso
 * durante un tiempo el PDF se fabricó fotografiando el informe. El Word existe:
 * es el de Microsoft, y el acta ya está subida donde puede verlo.
 *
 * Devuelve el blob, que es lo que quiere quien lo pide para guardarlo en su
 * disco. Cuando el PDF sí viaja —el que se adjunta en el correo de firma— se
 * usa `pdfDelDocumentoEnBase64`, que es esto mismo más la conversión.
 */
export async function pdfDelDocumento(token, { driveId, itemId }) {
  let respuesta;
  try {
    respuesta = await fetch(`${GRAFO}/drives/${driveId}/items/${itemId}/content?format=pdf`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  } catch (e) {
    // ── Por qué este `catch` y no dejar subir el error ───────────────────
    //
    // Porque cuando un `fetch` no llega a completarse, lo que lanza es un
    // `TypeError` cuyo mensaje es, literalmente, «Failed to fetch». Eso acababa
    // escrito en rojo bajo el botón de mandar a firmar: en inglés, sin decir qué
    // falló, y sin decir lo único que de verdad importaba ahí —que no se habia
    // avisado a nadie todavía—.
    //
    // Y no es un error de red cualquiera. Esta llamada responde con un 302 a
    // otro servidor —el de descarga de OneDrive—, y la política de seguridad
    // del sitio se aplica a CADA salto de la redirección: si al host de destino
    // no se le ha dado permiso en `connect-src`, el navegador corta la petición
    // y lo que se ve es exactamente este mismo «Failed to fetch». Pasa solo en
    // produccion, porque la politica la sirve la Static Web App y el servidor
    // de desarrollo no la pone: de ahi que algo probado y funcionando se
    // rompiera al publicarlo.
    //
    // Convertirlo en OneDriveError hace además que la pantalla lo cuente por la
    // rama buena, la que dice que no se ha mandado nada y se puede reintentar.
    throw new OneDriveError(
      'No se pudo contactar con Office para convertir el acta a PDF. '
      + 'Puede ser la conexión, o que el navegador haya bloqueado la descarga; '
      + 'si se repite, mira la consola del navegador. No se ha enviado nada.',
      { causa: e.message }
    );
  }

  if (!respuesta.ok) {
    throw new OneDriveError(
      `No se pudo convertir el acta a PDF (${respuesta.status}).`,
      { status: respuesta.status }
    );
  }

  return respuesta.blob();
}

/**
 * El mismo PDF, listo para mandarlo a la API.
 *
 * Existe porque el acta que se adjunta en el correo de firma la convierte el
 * NAVEGADOR y no el servidor. La conversión la hace Office sobre el archivo de
 * OneDrive, y quien tiene permiso para pedírsela es la persona que ha iniciado
 * sesión: la aplicación no tiene permisos propios sobre esos archivos, a
 * propósito —el porqué está en `authConfig.js`—. Así que se convierte aquí y se
 * suben los bytes.
 *
 * El nombre sale del propio documento, cambiándole la extensión: es lo que
 * verá quien firme en su bandeja de entrada, y «Acta de Dirección 12-03.pdf»
 * dice qué es sin abrirlo.
 */
export async function pdfDelDocumentoEnBase64(token, documento) {
  const blob = await pdfDelDocumento(token, documento);
  const bytes = new Uint8Array(await blob.arrayBuffer());

  // ── Que lo que vuelve sea de verdad el documento convertido ──────────
  //
  // `?format=pdf` responde con un 302 a una dirección de descarga de otro
  // servidor, y de ahí puede volver cualquier cosa con un 200 encima: una
  // página de error, un aviso de sesión caducada, un HTML. El `ok` de la
  // respuesta no lo distingue, y esto acaba adjuntado en el correo de quien
  // tiene que firmar, así que se mira lo único que no se puede fingir: la del
  // formato. Un PDF empieza por %PDF-.
  const cabecera = String.fromCharCode(...bytes.subarray(0, 5));
  if (cabecera !== '%PDF-') {
    throw new OneDriveError(
      'Office no devolvió un PDF al convertir el acta. No se ha adjuntado nada: '
      + 'vuelve a intentarlo en un momento.'
    );
  }

  // Un PDF de un acta con texto no baja de unas decenas de miles de bytes. Si
  // sale mucho más pequeño, lo que hay no es un acta: es una página de error
  // convertida, y adjuntarla sería mandar a firmar un documento en blanco.
  if (bytes.length < 1200) {
    throw new OneDriveError('La conversión a PDF devolvió un archivo vacío.');
  }

  const nombre = String((documento && documento.nombre) || 'acta')
    .replace(/\.(docx?|pdf)$/i, '') + '.pdf';

  return { nombre, base64: base64DeBytes(bytes), bytes: bytes.length };
}

/**
 * La URL para empotrar el documento en un iframe.
 *
 * `/preview` es la vía que Microsoft documenta para esto y devuelve una URL de
 * vida corta pensada para ir dentro de un iframe. La alternativa —colgar el
 * `webUrl` con `action=embedview`— apunta a una página de SharePoint que el
 * tenant puede negarse a dejar enmarcar, y el fallo es silencioso: un recuadro
 * en blanco, sin error.
 *
 * `editable` decide si Word abre en modo edición o de solo lectura. Editar pide
 * además permiso de escritura sobre el archivo: en la copia del propio OneDrive
 * se tiene, y en una biblioteca ajena puede no tenerse —ahí Word abrirá en
 * lectura por su cuenta, sin que esto falle—.
 */
export async function urlEmpotrada(token, { driveId, itemId }, { editable = true } = {}) {
  const respuesta = await fetch(`${GRAFO}/drives/${driveId}/items/${itemId}/preview`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ allowEdit: Boolean(editable) })
  });

  const datos = await leerRespuesta(respuesta);
  if (!datos || (!datos.getUrl && !datos.postUrl)) {
    throw new OneDriveError('OneDrive no devolvió una vista previa para este documento');
  }

  // Graph devuelve dos puertas al mismo documento y NO son equivalentes:
  //
  //   getUrl  — se abre con un GET normal, y es la de MIRAR.
  //   postUrl — hay que entrar por POST con `postParameters`, y es la de
  //             ESCRIBIR: la sesión de edición viaja en esos parámetros y no
  //             cabe en una dirección.
  //
  // Pedir `allowEdit: true` y luego usar getUrl era pedir permiso para editar y
  // abrir la puerta de mirar: el documento salía, pero en solo lectura. Cuando
  // se quiere editar, postUrl manda; getUrl queda de reserva por si el servicio
  // no la ofrece.
  const conPost = Boolean(datos.postUrl && editable);

  // `postParameters` viene como una cadena tipo formulario
  // ("access_token=…&access_token_ttl=…"), que es como hay que enviarla.
  const parametros = conPost ? parametrosDeFormulario(datos.postParameters) : null;

  return {
    modo: conPost ? 'post' : 'get',
    url: conPost ? datos.postUrl : datos.getUrl,
    parametros,
    // Cuándo vence esta sesión. Quien la use tiene que saberlo: la dirección
    // sigue siendo válida para siempre —es la del documento— pero el vale que
    // va dentro no, y entrar con uno vencido no da un error claro sino un
    // recuadro en blanco.
    caduca: caducidadDelVale(parametros),
    // Qué contestó el servicio, para poder distinguir «Graph no dio sesión de
    // edición» de «la dio y el navegador no la deja funcionar». Sin esto, las
    // dos causas se ven igual desde fuera —el documento sale y no deja
    // escribir— y se arregla la que no era. No lleva el token.
    diagnostico: {
      pedidoEditable: Boolean(editable),
      ofreceEdicion: Boolean(datos.postUrl),
      ofreceLectura: Boolean(datos.getUrl),
      campos: Object.keys(datos || {}),
      // El nombre del campo no basta: Graph devuelve `postUrl` presente y vacío,
      // que es distinto de no devolverlo y lleva a conclusiones opuestas.
      formas: {
        getUrl: formaDelValor(datos.getUrl),
        postUrl: formaDelValor(datos.postUrl),
        postParameters: formaDelValor(datos.postParameters)
      },
      // Sin la parte de la consulta: ahí es donde viajarían credenciales.
      destinos: {
        getUrl: sinConsulta(datos.getUrl),
        postUrl: sinConsulta(datos.postUrl)
      }
    }
  };
}

/**
 * Cuándo vence la sesión de edición, en milisegundos desde 1970.
 *
 * WOPI la manda junto al vale, en `access_token_ttl`. Vale 0 cuando el
 * servicio no lo dice; entonces se devuelve null, que aquí significa «no se
 * sabe» y no «no caduca»: darla por eterna sería volver al fallo de entrar con
 * un vale muerto.
 */
function caducidadDelVale(parametros) {
  if (!parametros) return null;
  const par = parametros.find(([nombre]) => nombre === 'access_token_ttl');
  const ms = par ? Number(par[1]) : 0;
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

/** Qué es un valor, sin enseñarlo: "cadena(0)", "nulo", "ausente". */
function formaDelValor(v) {
  if (v === undefined) return 'ausente';
  if (v === null) return 'nulo';
  if (typeof v === 'string') return `cadena(${v.length})`;
  return typeof v;
}

/** Una URL sin su parte de consulta, que es donde viajan las credenciales. */
function sinConsulta(v) {
  if (typeof v !== 'string' || !v) return null;
  try {
    const u = new URL(v);
    return `${u.origin}${u.pathname}`;
  } catch {
    return '(no es una URL)';
  }
}

/**
 * Los permisos que lleva un token, leídos del propio token.
 *
 * Un token de Graph es un JWT: tres partes separadas por puntos, y la de en
 * medio es un JSON con los datos de la sesión. El campo `scp` enumera los
 * permisos concedidos.
 *
 * Existe para poder distinguir «el servicio no quiso» de «la sesión no tiene
 * permiso para escribir», que producen exactamente el mismo síntoma —el acta se
 * ve y no se deja editar— y llevan a arreglar cosas distintas. Es la pregunta
 * que más veces se ha respondido mal en esta pantalla.
 *
 * No verifica la firma ni le hace falta: no se decide nada con esto, solo se
 * explica. Y no se mira nada más del token.
 */
export function permisosDelToken(token) {
  try {
    const medio = String(token).split('.')[1];
    if (!medio) return null;
    const normal = medio.replace(/-/g, '+').replace(/_/g, '/');
    const relleno = normal + '='.repeat((4 - (normal.length % 4)) % 4);
    const datos = JSON.parse(decodeURIComponent(escape(atob(relleno))));
    if (!datos || typeof datos.scp !== 'string') return null;
    return datos.scp.split(' ').filter(Boolean);
  } catch {
    // Un token que no se deja leer no es un fallo: simplemente no se puede
    // explicar nada a partir de él.
    return null;
  }
}

/** ¿Esta sesión puede escribir en los archivos de quien la abrió? */
export function puedeEscribirArchivos(token) {
  const permisos = permisosDelToken(token);
  if (!permisos) return null;
  return permisos.some(p => p === 'Files.ReadWrite' || p === 'Files.ReadWrite.All');
}

/**
 * Parte los `postParameters` de Graph en pares para el formulario.
 *
 * Llegan ya codificados, en una sola cadena. Se descodifican aquí porque el
 * navegador los volverá a codificar al enviar el formulario: dejarlos como
 * vienen los codificaría dos veces y el servicio rechazaría la sesión.
 */
function parametrosDeFormulario(crudo) {
  const pares = [];
  if (!crudo) return pares;
  for (const trozo of String(crudo).split('&')) {
    if (!trozo) continue;
    const corte = trozo.indexOf('=');
    const nombre = corte === -1 ? trozo : trozo.slice(0, corte);
    const valor = corte === -1 ? '' : trozo.slice(corte + 1);
    try {
      pares.push([decodeURIComponent(nombre), decodeURIComponent(valor)]);
    } catch {
      pares.push([nombre, valor]);
    }
  }
  return pares;
}

const actaPublicada = {
  CARPETA, nombreDeArchivo, sufijoDeFecha, bytesDeBase64, partesDelSitio,
  bibliotecaConfigurada, publicarParaVer, publicarEnBiblioteca, bajarDocumento,
  selloDeDocumento,
  urlEmpotrada, pdfDelDocumento, pdfDelDocumentoEnBase64, base64DeBytes,
  permisosDelToken, puedeEscribirArchivos, OneDriveError
};
export default actaPublicada;
